import { cn } from '@workspace/ui/lib';
import type * as React from 'react';

interface InputProps extends Omit<React.ComponentProps<'input'>, 'size'> {
  size?: 'sm' | 'base';
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  noSpaces?: boolean;
  lettersOnly?: boolean;
  numberOnly?: boolean;
  alphanumericOnly?: boolean;
  /** Only used when `type="number"` (ignored for other types). */
  allowNegativeValues?: boolean;
  /** Only used when `type="number"` (ignored for other types). */
  allowSpecialCharacters?: boolean;
  parentClassName?: string;
  readOnly?: boolean;
  capitalizeFirstLetter?: boolean;
  /** Prevents Enter from submitting parent forms (e.g. debounced search in table toolbars). */
  preventEnterSubmit?: boolean;
}

function Input({
  className,
  parentClassName,
  size = 'base',
  type,
  placeholder,
  startIcon,
  endIcon,
  noSpaces,
  lettersOnly,
  numberOnly,
  alphanumericOnly,
  allowNegativeValues,
  allowSpecialCharacters,
  onBeforeInput,
  readOnly = false,
  onBlur,
  onChange,
  onKeyDown,
  capitalizeFirstLetter = true,
  preventEnterSubmit = false,
  disabled,
  min = undefined,
  max = undefined,
  ...props
}: InputProps) {
  const resolvedPlaceholder = readOnly ? 'Not provided' : (placeholder ?? 'Type here');

  const handleBeforeInput = (e: React.FormEvent<HTMLInputElement>) => {
    const native = e.nativeEvent as InputEvent;
    const input = native.data;

    if (!input) return;

    if (type === 'number') {
      if (allowNegativeValues === false && input.includes('-')) {
        e.preventDefault();
        return;
      }

      if (allowSpecialCharacters === false && /[+eE]/.test(input)) {
        e.preventDefault();
        return;
      }
    }

    if (noSpaces && /\s/.test(input)) {
      e.preventDefault();
    }

    if (lettersOnly && /[^\p{L}\p{M}\s]/u.test(input)) {
      e.preventDefault();
    }

    if (numberOnly && /[^0-9]/.test(input)) {
      e.preventDefault();
    }

    if (alphanumericOnly && /[^a-zA-Z0-9]/.test(input)) {
      e.preventDefault();
    }

    if (onBeforeInput) {
      onBeforeInput(e as unknown as React.InputEvent<HTMLInputElement>);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const inputType = type || 'text';
    if (inputType === 'text' && capitalizeFirstLetter) {
      const input = e.currentTarget;
      const originalValue = input.value;

      if (originalValue.length > 0) {
        const firstChar = originalValue.charAt(0);
        const capitalizedValue = firstChar.toUpperCase() + originalValue.slice(1);

        if (originalValue !== capitalizedValue) {
          // Defer the update using requestAnimationFrame to ensure blur completes first
          requestAnimationFrame(() => {
            // Use native value setter to update the input
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
              window.HTMLInputElement.prototype,
              'value'
            )?.set;

            if (nativeInputValueSetter) {
              nativeInputValueSetter.call(input, capitalizedValue);
            } else {
              input.value = capitalizedValue;
            }

            if (onChange) {
              const changeEvent = {
                target: input,
                currentTarget: input,
              } as React.ChangeEvent<HTMLInputElement>;
              onChange(changeEvent);
            }
          });
        }
      }
    }

    if (onBlur) {
      onBlur(e);
    }
  };

  // Only attach keydown when needed: avoids running any search/Enter logic on every Input in the app.
  const numberKeyRestrictions =
    type === 'number' && (allowNegativeValues === false || allowSpecialCharacters === false);
  const mergedOnKeyDown =
    preventEnterSubmit || onKeyDown || numberKeyRestrictions
      ? (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (preventEnterSubmit && e.key === 'Enter') {
            e.preventDefault();
          }
          if (
            numberKeyRestrictions &&
            allowNegativeValues === false &&
            (e.key === '-' || e.key === 'Subtract')
          ) {
            e.preventDefault();
          }
          if (
            numberKeyRestrictions &&
            allowSpecialCharacters === false &&
            (e.key === '+' || e.key === 'Add' || e.key === 'e' || e.key === 'E')
          ) {
            e.preventDefault();
          }
          onKeyDown?.(e);
        }
      : undefined;

  return (
    <div className={cn('relative', parentClassName)}>
      <input
        type={type}
        data-slot="input"
        placeholder={resolvedPlaceholder}
        disabled={disabled}
        min={min}
        max={max}
        className={cn(
          'peer [&:not(:placeholder-shown):not(:disabled)]:text-primary placeholder:text-grey-700 dark:placeholder:text-grey-500 dark:disabled:placeholder:text-grey-900 dark:disabled:text-grey-900 file:text-foreground selection:bg-grey-100 dark:text-grey-700 selection:text-primary-foreground disabled:border-grey-100 dark:disabled:border-grey-900 disabled:placeholder:text-grey-300 disabled:text-grey-300 disabled:outline-grey-100 aria-invalid:border-destructive dark:[&:not(:placeholder-shown)]:not(:disabled)]:text-neutral-100 flex w-full min-w-0 border bg-transparent shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:outline-1 disabled:outline-offset-[-1px] dark:hover:border-neutral-100 dark:hover:text-neutral-100 dark:aria-invalid:border-red-400 dark:[&:not(:placeholder-shown):not(:disabled)]:text-white',
          size === 'sm'
            ? 'h-9 rounded-xl px-3 py-2.5 text-sm'
            : 'h-12 rounded-2xl px-4 py-3 text-base',
          startIcon && (size === 'sm' ? 'pl-8' : 'pl-10'),
          endIcon && 'pr-10',
          readOnly
            ? 'text-grey-600! border-grey-300 outline-grey-300 hover:border-grey-300 focus-visible:border-grey-300 active:border-grey-300 dark:border-grey-700 pointer-events-none cursor-default shadow-none outline-1 outline-offset-[-1px]'
            : 'border-grey-300 hover:border-primary focus-visible:border-primary dark:border-grey-700',
          type === 'number' &&
            'appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
          className
        )}
        {...(readOnly &&
        ['text', 'search', 'tel', 'url', 'email', 'password'].includes(type || 'text')
          ? { 'aria-readonly': 'true' }
          : {})}
        readOnly={readOnly}
        onBeforeInput={handleBeforeInput}
        onChange={onChange}
        onBlur={handleBlur}
        {...props}
        onKeyDown={mergedOnKeyDown}
      />
      {startIcon && (
        <div
          className={cn(
            'text-grey-700 absolute top-1/2 left-4 flex h-4 w-4 -translate-y-1/2 items-center justify-center [&>svg]:h-5 [&>svg]:w-5',
            readOnly && 'text-grey-700 dark:text-neutral-100',
            disabled && 'text-grey-300 dark:text-grey-900',
            size === 'sm' && 'left-3'
          )}
        >
          {startIcon}
        </div>
      )}
      {endIcon && (
        <div
          className={cn(
            'text-grey-700 absolute top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center [&>svg]:h-5 [&>svg]:w-5',
            size === 'sm' ? 'right-3' : 'right-4',
            readOnly && 'text-grey-700 dark:text-neutral-100',
            disabled && 'text-grey-300 dark:text-grey-900'
          )}
        >
          {endIcon}
        </div>
      )}
    </div>
  );
}

export { Input };
